// Google Tag Manager Loader - Fixed Version
export const loadGoogleTagManager = () => {
  const gtmId = import.meta.env?.VITE_GTM_ID;
  
  if (!gtmId) {
    console.warn('GTM ID not found in environment variables');
    return;
  }
  
  // Check if GTM is already loaded
  if (window.dataLayer && window.google_tag_manager) {
    console.log('GTM already loaded');
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  
  // Push GTM start event
  window.dataLayer.push({ 
    'gtm.start': new Date().getTime(), 
    event: 'gtm.js' 
  });
  
  // Add noscript iframe fallback
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
  
  // Create and load GTM script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  
  // Handle successful load
  script.onload = () => {
    console.log('Google Tag Manager script loaded successfully');
    
    // Check if GTM initialized properly after a short delay
    setTimeout(() => {
      if (window.google_tag_manager) {
        console.log('Google Tag Manager initialized successfully');
      } else {
        console.warn('GTM script loaded but manager object not found - this is normal in some configurations');
      }
    }, 1000);
  };
  
  // Handle loading errors
  script.onerror = () => {
    console.error('Failed to load Google Tag Manager script');
    
    // Only show error message if we're in production
    if (!import.meta.env.DEV) {
      const fallback = document.createElement('div');
      fallback.textContent = 'Analytics features are currently unavailable.';
      fallback.style.cssText =
        'position:fixed;bottom:0;left:0;right:0;padding:8px;background:#f8d7da;color:#721c24;text-align:center;font-size:14px;z-index:9999;';
      fallback.id = 'gtm-error-banner';
      
      // Remove existing error banner if present
      const existing = document.getElementById('gtm-error-banner');
      if (existing) existing.remove();
      
      document.body.appendChild(fallback);
      
      // Auto-hide after 10 seconds
      setTimeout(() => {
        const banner = document.getElementById('gtm-error-banner');
        if (banner) banner.remove();
      }, 10000);
    }
  };
  
  // Append script to head
  document.head.appendChild(script);
  
  // Log GTM ID for debugging
  console.log(`Attempting to load GTM with ID: ${gtmId}`);
};

// Enhanced event pushing with validation
export const pushToDataLayer = (eventData) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    // Ensure we have at least event name
    if (!eventData.event) {
      console.warn('GTM: Event name is required');
      return;
    }
    
    window.dataLayer.push(eventData);
    
    // Only log in development mode
    if (import.meta.env.DEV) {
      console.log('GTM Event pushed:', eventData);
    }
  } else if (import.meta.env.DEV) {
    console.warn('DataLayer not available, event not pushed:', eventData);
  }
};

// Track form step progression
export const trackFormStep = (stepNumber, stepName, totalSteps) => {
  pushToDataLayer({
    event: 'form_step_view',
    form_name: 'landing_page_order',
    step_number: stepNumber,
    step_name: stepName,
    total_steps: totalSteps,
    step_percentage: Math.round((stepNumber / totalSteps) * 100)
  });
};

// Track form field interaction
export const trackFormField = (fieldName, fieldType, hasValue) => {
  pushToDataLayer({
    event: 'form_field_interaction',
    form_name: 'landing_page_order',
    field_name: fieldName,
    field_type: fieldType,
    field_has_value: hasValue
  });
};

// Track form abandonment
export const trackFormAbandonment = (stepNumber, stepName, timeSpent) => {
  pushToDataLayer({
    event: 'form_abandonment',
    form_name: 'landing_page_order',
    abandonment_step: stepNumber,
    abandonment_step_name: stepName,
    time_spent_seconds: Math.round(timeSpent / 1000)
  });
};

// Track successful form submission
export const trackFormSubmission = (formData) => {
  pushToDataLayer({
    event: 'form_submission',
    form_name: 'landing_page_order',
    business_type: formData.businessType || 'not_specified',
    has_physical_store: formData.hasPhysicalStore || 'no',
    main_goal: formData.mainGoal || 'not_specified',
    // Add conversion value if needed
    value: 1000,
    currency: 'ILS'
  });
};

// Track page view with custom parameters
export const trackPageView = (pageName, pageCategory) => {
  pushToDataLayer({
    event: 'page_view_custom',
    page_name: pageName,
    page_category: pageCategory || 'general'
  });
};

// Initialize GTM when DOM is ready
export const initGTM = () => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadGoogleTagManager);
  } else {
    loadGoogleTagManager();
  }
};

// Scroll Tracking
let lastScrollPercentage = 0;
export const initScrollTracking = () => {
  let ticking = false;
  
  const updateScrollProgress = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    const scrollPercentage = Math.round((scrollTop / scrollHeight) * 100);
    
    // Track significant scroll milestones
    const milestones = [25, 50, 75, 90, 100];
    milestones.forEach(milestone => {
      if (scrollPercentage >= milestone && lastScrollPercentage < milestone) {
        pushToDataLayer({
          event: 'scroll_milestone',
          scroll_depth: milestone,
          page_section: getPageSection(scrollPercentage)
        });
      }
    });
    
    lastScrollPercentage = scrollPercentage;
    ticking = false;
  };
  
  const requestTick = () => {
    if (!ticking) {
      window.requestAnimationFrame(updateScrollProgress);
      ticking = true;
    }
  };
  
  window.addEventListener('scroll', requestTick);
};

// Get current page section based on scroll
const getPageSection = (scrollPercentage) => {
  if (scrollPercentage < 25) return 'hero';
  if (scrollPercentage < 50) return 'benefits';
  if (scrollPercentage < 75) return 'testimonials';
  return 'form';
};

// User Timing for form steps
export const trackStepTiming = (stepNumber, stepName, timeSpent) => {
  pushToDataLayer({
    event: 'timing_complete',
    timing_category: 'form_interaction',
    timing_var: `step_${stepNumber}_${stepName}`,
    timing_value: timeSpent,
    timing_label: stepName
  });
  
  // Also send to GA4 as custom metric if gtag is available
  if (window.gtag) {
    window.gtag('event', 'timing', {
      name: `form_step_${stepNumber}`,
      value: timeSpent,
      metric_name: 'step_completion_time',
      step_name: stepName
    });
  }
};

// Error Tracking
export const trackError = (errorType, errorMessage, errorLocation) => {
  pushToDataLayer({
    event: 'error_occurred',
    error_type: errorType,
    error_message: errorMessage,
    error_location: errorLocation,
    error_severity: getSeverity(errorType)
  });
};

const getSeverity = (errorType) => {
  const criticalErrors = ['form_submission_failed', 'payment_error', 'api_error'];
  const warningErrors = ['validation_error', 'file_upload_error'];
  
  if (criticalErrors.includes(errorType)) return 'critical';
  if (warningErrors.includes(errorType)) return 'warning';
  return 'info';
};

// Enhanced Form Field Tracking
export const trackFieldEngagement = (fieldName, engagementType, fieldValue) => {
  const engagementData = {
    event: 'field_engagement',
    field_name: fieldName,
    engagement_type: engagementType, // focus, blur, change, clear
    field_has_value: !!fieldValue,
    field_length: typeof fieldValue === 'string' ? fieldValue.length : 0
  };
  
  // Track specific patterns (without exposing PII)
  if (fieldName === 'email' && fieldValue) {
    const domain = fieldValue.split('@')[1];
    if (domain) {
      engagementData.email_domain = domain;
    }
  }
  
  if (fieldName === 'phone' && fieldValue) {
    engagementData.phone_prefix = fieldValue.substring(0, 3);
  }
  
  pushToDataLayer(engagementData);
};